import { useState } from "react";
import { Link } from "react-router-dom";

import { api, authBase } from "../api/client";
import { useMe } from "../api/me";
import { isAdmin } from "../lib/admin";
import FeedbackDialog from "./FeedbackDialog";

/** Shared brand row rendered above TopNav on every route.
 *
 * The "stux" wordmark is non-semantic on purpose — page-specific routes
 * (Hindsight, ticker detail, etc.) own the document's <h1>. This is just
 * a brand+actions row.
 */
export default function PageHeader() {
  const me = useMe();
  const showAdmin = isAdmin(me.data?.email);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const handleLogout = () => {
    api.post(`${authBase}/logout`).finally(() => {
      window.location.href = "/login";
    });
  };

  return (
    <header className="mb-4 flex items-center justify-between gap-3 sm:mb-6">
      <Link
        to="/"
        className="text-xl font-semibold tracking-tight text-slate-100 transition hover:text-sky-300 sm:text-2xl"
        aria-label="stux home"
      >
        stux
      </Link>
      <div className="flex items-center gap-2">
        {me.data?.authenticated && (
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="hidden rounded-full border border-slate-800 px-3 py-1 text-[11px] uppercase tracking-wide text-slate-400 transition hover:border-sky-500/40 hover:text-sky-300 sm:inline-flex"
            aria-label="Send feedback"
          >
            Send feedback
          </button>
        )}
        {me.data?.authenticated && (
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            aria-label="Send feedback"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-800 hover:text-slate-200 sm:hidden"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M3.5 4A1.5 1.5 0 0 0 2 5.5v8A1.5 1.5 0 0 0 3.5 15h2v2.5a.5.5 0 0 0 .82.39L9.7 15H16.5A1.5 1.5 0 0 0 18 13.5v-8A1.5 1.5 0 0 0 16.5 4h-13Z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
        {showAdmin && (
          <Link
            to="/telemetry"
            className="rounded-full px-2 py-1 text-[10px] uppercase tracking-wide text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
            aria-label="Admin telemetry"
          >
            Admin
          </Link>
        )}
        <Link
          to="/settings"
          aria-label="Settings"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-800 hover:text-slate-200 sm:h-9 sm:w-auto sm:px-3 sm:text-xs"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 sm:hidden" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M7.84 1.804A1 1 0 0 1 8.82 1h2.36a1 1 0 0 1 .98.804l.331 1.652a6.993 6.993 0 0 1 1.929 1.115l1.598-.54a1 1 0 0 1 1.186.447l1.18 2.044a1 1 0 0 1-.205 1.251l-1.267 1.113a7.047 7.047 0 0 1 0 2.228l1.267 1.114a1 1 0 0 1 .206 1.25l-1.18 2.045a1 1 0 0 1-1.187.447l-1.598-.54a6.993 6.993 0 0 1-1.929 1.115l-.33 1.652a1 1 0 0 1-.98.804H8.82a1 1 0 0 1-.98-.804l-.331-1.652a6.993 6.993 0 0 1-1.929-1.115l-1.598.54a1 1 0 0 1-1.186-.447l-1.18-2.044a1 1 0 0 1 .205-1.251l1.267-1.114a7.05 7.05 0 0 1 0-2.227L1.821 8.773a1 1 0 0 1-.206-1.25l1.18-2.045a1 1 0 0 1 1.187-.447l1.598.54A6.993 6.993 0 0 1 7.51 4.456l.33-1.652ZM10 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
              clipRule="evenodd"
            />
          </svg>
          <span className="hidden sm:inline">Settings</span>
        </Link>
        <button
          onClick={handleLogout}
          aria-label="Sign out"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-800 hover:text-slate-200 sm:h-9 sm:w-auto sm:px-3 sm:text-xs"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 sm:hidden" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M3 4.75A2.75 2.75 0 0 1 5.75 2h5.5A2.75 2.75 0 0 1 14 4.75V6a.75.75 0 0 1-1.5 0V4.75c0-.69-.56-1.25-1.25-1.25h-5.5c-.69 0-1.25.56-1.25 1.25v10.5c0 .69.56 1.25 1.25 1.25h5.5c.69 0 1.25-.56 1.25-1.25V14a.75.75 0 0 1 1.5 0v1.25A2.75 2.75 0 0 1 11.25 18h-5.5A2.75 2.75 0 0 1 3 15.25V4.75ZM13.97 6.97a.75.75 0 0 1 1.06 0l2.5 2.5a.75.75 0 0 1 0 1.06l-2.5 2.5a.75.75 0 1 1-1.06-1.06L15.19 10.75H8.75a.75.75 0 0 1 0-1.5h6.44l-1.22-1.22a.75.75 0 0 1 0-1.06Z"
              clipRule="evenodd"
            />
          </svg>
          <span className="hidden sm:inline">Sign out</span>
        </button>
      </div>
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </header>
  );
}
